"use client";

import { useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/analytics";
import {
  climateSelection,
  climateSelectionUrl,
} from "@/lib/climate/view-state";

const CHANGE_EVENT = "peakwave:climate-view";
function subscribe(callback: () => void) {
  window.addEventListener("popstate", callback);
  window.addEventListener(CHANGE_EVENT, callback);
  return () => {
    window.removeEventListener("popstate", callback);
    window.removeEventListener(CHANGE_EVENT, callback);
  };
}

/** Stable server defaults keep all public evidence in HTML; URL selection hydrates afterwards. */
export function useClimateState<T extends string>(
  key: string,
  fallback: T,
  allowed: readonly T[],
) {
  const value = useSyncExternalStore(
    subscribe,
    () => climateSelection(window.location.search, key, fallback, allowed),
    () => fallback,
  );
  function setValue(next: T) {
    const href = climateSelectionUrl(window.location.href, key, next, allowed);
    if (!href) return;
    window.history.replaceState(window.history.state, "", href);
    window.dispatchEvent(new Event(CHANGE_EVENT));
    trackEvent("climate_explore_action", { control: key, selection: next });
  }
  return [value, setValue] as const;
}

export function useClimateIndex(key: string, fallback: number, count: number) {
  const allowed = Array.from({ length: count }, (_, index) => String(index));
  const [value, setValue] = useClimateState(key, String(fallback), allowed);
  return [Number(value), (next: number) => setValue(String(next))] as const;
}
